import React, { useEffect, useMemo, useState } from 'react';
import Navbar from '../components/Navbar';
import Pagination from '../components/Pagination';
import usePagination from '../hooks/usePagination';
import api from '../services/api';
import toast, { Toaster } from 'react-hot-toast';
import FacePhotosModal from '../components/FacePhotosModal';
import {
  getFaceConfig,
  updateFaceConfig,
  getFaceStatus,
  getRecentVerifications,
  getVerificationStats,
  deleteVerification,
  deleteVerifications,
  fetchVerificationPhotoUrl,
} from '../services/faceApi';
import './Global.css';
import './FaceConfigPage.css';

/**
 * Tela admin da verificação facial:
 *  - liga/desliga a feature e a OBRIGATORIEDADE (enforcement no backend);
 *  - ajusta o rigor (threshold de similaridade);
 *  - acompanha a cobertura de cadastro (quem tem/não tem as 3 fotos) e
 *    gerencia as fotos de qualquer pessoa (privativo do admin: o fiscal só
 *    verifica quem já está cadastrado);
 *  - audita as últimas verificações (aprovadas e RECUSADAS) com foto e
 *    permite excluí-las, uma a uma ou em lote por idade.
 */
const FaceConfigPage = () => {
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [faceStatus, setFaceStatus] = useState({});
  const [search, setSearch] = useState('');
  const [coverageFilter, setCoverageFilter] = useState('all'); // all | with | without
  const [managePerson, setManagePerson] = useState(null);
  const [verifications, setVerifications] = useState([]);
  const [verificationStats, setVerificationStats] = useState(null);
  const [purgeDays, setPurgeDays] = useState('90');
  const [purging, setPurging] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [evidenceUrl, setEvidenceUrl] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    try {
      const [cfg, status, usersRes, recent, stats] = await Promise.all([
        getFaceConfig(),
        getFaceStatus(),
        api.get('/users'),
        getRecentVerifications(50),
        getVerificationStats(),
      ]);
      setConfig(cfg);
      setFaceStatus(status);
      setVerificationStats(stats);
      // Mesmo recorte do FiscalDashboard: quem registra ponto.
      setUsers(
        usersRes.data.filter(
          (u) =>
            u.isActive &&
            (u.personnelType === 'civil' || (u.horasAulas != null && u.horasAulas > 0)),
        ),
      );
      setVerifications(recent);
    } catch (error) {
      toast.error(
        'Erro ao carregar dados: ' +
          (error.response?.data?.message || error.message),
      );
    } finally {
      setLoading(false);
    }
  };

  const refreshStatus = async () => {
    try {
      setFaceStatus(await getFaceStatus());
    } catch (e) {
      /* mantém o estado atual */
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await updateFaceConfig({
        enabled: config.enabled,
        required: config.required,
        threshold: Number(config.threshold),
      });
      setConfig(updated);
      toast.success('Configuração facial salva!');
    } catch (error) {
      toast.error(
        'Erro ao salvar: ' + (error.response?.data?.message || error.message),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleShowEvidence = async (verification) => {
    try {
      const url = await fetchVerificationPhotoUrl(verification.id);
      // Revoga o URL anterior antes de trocar (duplo clique / troca de foto
      // criava blobs que nunca eram liberados).
      setEvidenceUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return url;
      });
    } catch (error) {
      toast.error('Foto de evidência indisponível');
    }
  };

  const refreshVerifications = async () => {
    const [recent, stats] = await Promise.all([
      getRecentVerifications(50),
      getVerificationStats(),
    ]);
    setVerifications(recent);
    setVerificationStats(stats);
  };

  const handleDeleteVerification = async (verification) => {
    const quando = formatDateTime(verification.createdAt);
    const quem = verification.user?.name || verification.userId;
    if (
      !window.confirm(
        `Excluir a verificação de ${quem} em ${quando}?\n\n` +
          'A foto de evidência é apagada do servidor e a trilha desta ' +
          'verificação deixa de existir. A ação é registrada na auditoria e ' +
          'não pode ser desfeita.',
      )
    ) {
      return;
    }
    setDeletingId(verification.id);
    try {
      await deleteVerification(verification.id);
      await refreshVerifications();
      toast.success('Verificação excluída');
    } catch (error) {
      toast.error(
        'Erro ao excluir: ' + (error.response?.data?.message || error.message),
      );
    } finally {
      setDeletingId(null);
    }
  };

  const handlePurge = async () => {
    const days = Number(purgeDays);
    const alvo =
      days > 0
        ? `todas as verificações com mais de ${days} dias`
        : 'TODAS as verificações já registradas';
    if (
      !window.confirm(
        `Excluir ${alvo}?\n\n` +
          'As fotos de evidência correspondentes são apagadas do servidor. ' +
          'A ação é registrada na auditoria e não pode ser desfeita.',
      )
    ) {
      return;
    }
    setPurging(true);
    try {
      const result = await deleteVerifications(days);
      await refreshVerifications();
      toast.success(
        result.removed > 0
          ? `${result.removed} verificação(ões) excluída(s)`
          : 'Nenhuma verificação no período informado',
      );
    } catch (error) {
      toast.error(
        'Erro no expurgo: ' + (error.response?.data?.message || error.message),
      );
    } finally {
      setPurging(false);
    }
  };

  // Libera o blob da evidência ao desmontar (sair da página com o modal aberto).
  useEffect(() => {
    return () => {
      if (evidenceUrl) URL.revokeObjectURL(evidenceUrl);
    };
  }, [evidenceUrl]);

  const enrolledCount = useMemo(
    () => users.filter((u) => (faceStatus[u.id] || 0) > 0).length,
    [users, faceStatus],
  );

  const visibleUsers = useMemo(() => {
    const term = search.toLowerCase();
    return users
      .filter((u) => {
        const enrolled = (faceStatus[u.id] || 0) > 0;
        if (coverageFilter === 'with' && !enrolled) return false;
        if (coverageFilter === 'without' && enrolled) return false;
        if (!term) return true;
        return (
          u.name.toLowerCase().includes(term) ||
          (u.matricula || '').toLowerCase().includes(term)
        );
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [users, faceStatus, search, coverageFilter]);

  const peoplePagination = usePagination(visibleUsers, 10);
  const verificationsPagination = usePagination(verifications, 25);
  const { setPage: setPeoplePage } = peoplePagination;

  // Busca/filtro mudou: a paginação volta pro começo da lista nova.
  useEffect(() => {
    setPeoplePage(1);
  }, [search, coverageFilter, setPeoplePage]);

  const formatDateTime = (value) => {
    if (!value) return '-';
    const d = new Date(value);
    return d.toLocaleDateString('pt-BR') + ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="container">
          <div className="loading-container">
            <div className="loading-spinner" />
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Toaster position="top-right" />
      <Navbar />
      <div className="container">
        <div className="page-header-new">
          <div>
            <h1>Verificação Facial</h1>
            <p className="subtitle">
              Cadastro de fotos, obrigatoriedade e auditoria das verificações no ponto
            </p>
          </div>
        </div>

        {/* ------------------------------------------------ configuração */}
        {config && (
          <div className="info-card facepg-card">
            <div className="info-card-body">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={config.enabled}
                  onChange={(e) => setConfig({ ...config, enabled: e.target.checked })}
                />
                <span>
                  <strong>Verificação facial ativada</strong> — o fiscal vê a opção de
                  verificar a identidade pela câmera na entrada e na saída
                </span>
              </label>

              <label className="checkbox-label" style={{ marginTop: 10 }}>
                <input
                  type="checkbox"
                  checked={config.required}
                  disabled={!config.enabled}
                  onChange={(e) => setConfig({ ...config, required: e.target.checked })}
                />
                <span>
                  <strong>Obrigatória</strong> — o servidor RECUSA entrada/saída de quem
                  tem fotos cadastradas sem a verificação aprovada (quem ainda não tem
                  fotos não é bloqueado; acompanhe a cobertura abaixo)
                </span>
              </label>

              <div className="facepg-threshold">
                <label htmlFor="facepg-threshold-input">
                  Rigor da comparação (similaridade mínima):{' '}
                  <strong>{Math.round((config.threshold || 0.5) * 100)}%</strong>
                </label>
                <input
                  id="facepg-threshold-input"
                  type="range"
                  min="0.3"
                  max="0.9"
                  step="0.05"
                  value={config.threshold}
                  disabled={!config.enabled}
                  onChange={(e) => setConfig({ ...config, threshold: e.target.value })}
                />
                <small className="form-hint">
                  50% é o padrão calibrado. Acima disso fica mais rígido (pode recusar a
                  própria pessoa em iluminação ruim); abaixo, mais permissivo.
                </small>
                {Number(config.threshold) > 0.55 && (
                  <small className="form-hint" style={{ color: '#b45309', fontWeight: 600 }}>
                    ⚠ Acima de 55% as recusas legítimas aumentam bastante. Recomendado: 50%.
                  </small>
                )}
              </div>

              <button className="btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Salvando...' : 'Salvar configuração'}
              </button>
            </div>
          </div>
        )}

        {/* ------------------------------------------------ cobertura */}
        <div className="facepg-section-header">
          <h2>Cobertura de cadastro</h2>
          <span className="facepg-coverage">
            {enrolledCount} de {users.length} pessoas com fotos cadastradas
          </span>
        </div>
        <div className="table-controls">
          <div className="search-bar">
            <input
              className="search-input"
              placeholder="Buscar por nome ou matrícula..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="role-filter">
            <select
              className="role-select"
              value={coverageFilter}
              onChange={(e) => setCoverageFilter(e.target.value)}
            >
              <option value="all">Todos</option>
              <option value="with">Com fotos</option>
              <option value="without">Sem fotos</option>
            </select>
          </div>
        </div>
        <div className="table-wrapper">
          <table className="modern-table">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Matrícula</th>
                <th>Tipo</th>
                <th>Fotos</th>
                <th style={{ textAlign: 'right' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {visibleUsers.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center empty-state">
                    <strong>Nenhuma pessoa encontrada</strong>
                    Ajuste a busca ou o filtro de cobertura de fotos.
                  </td>
                </tr>
              ) : (
                peoplePagination.pageItems.map((user) => {
                  const count = faceStatus[user.id] || 0;
                  return (
                    <tr key={user.id}>
                      <td className="cell-primary">{user.name}</td>
                      <td><span className="matricula-text">{user.matricula || '-'}</span></td>
                      <td>
                        <span className={`badge ${user.personnelType === 'civil' ? 'badge-civil' : 'badge-militar'}`}>
                          {user.personnelType === 'civil' ? 'Civil' : 'Militar'}
                        </span>
                      </td>
                      <td>
                        {count > 0 ? (
                          <span className="badge badge-success">✔ {count} foto{count > 1 ? 's' : ''}</span>
                        ) : (
                          <span className="badge badge-warning">Sem cadastro</span>
                        )}
                      </td>
                      <td className="cell-actions" style={{ textAlign: 'right' }}>
                        <button
                          className="btn-secondary btn-sm"
                          onClick={() => setManagePerson({ id: user.id, name: user.name })}
                        >
                          {count > 0 ? 'Gerenciar fotos' : 'Cadastrar fotos'}
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          id="face-pessoas"
          page={peoplePagination.page}
          totalPages={peoplePagination.totalPages}
          total={peoplePagination.total}
          from={peoplePagination.from}
          to={peoplePagination.to}
          pageSize={peoplePagination.pageSize}
          onPageChange={peoplePagination.setPage}
          onPageSizeChange={peoplePagination.setPageSize}
          itemLabel="pessoas"
        />

        {/* ------------------------------------------------ verificações */}
        <div className="facepg-section-header">
          <h2>Últimas verificações</h2>
          <button className="btn-secondary btn-sm" onClick={loadAll}>
            Atualizar
          </button>
        </div>

        <div className="info-card info-card-blue facepg-retention">
          <div className="info-card-body">
            <p>
              <strong>O que fica guardado:</strong> toda verificação — aprovada ou
              recusada — vira uma linha permanente (pessoa, entrada/saída,
              semelhança, fiscal e data). A <strong>foto de evidência</strong> é
              apagada automaticamente do servidor após <strong>90 dias</strong>;
              a linha continua como trilha de auditoria até ser excluída aqui.
            </p>
            {verificationStats && (
              <p>
                Hoje: <strong>{verificationStats.total}</strong> verificação(ões)
                registrada(s), <strong>{verificationStats.withPhoto}</strong> com foto
                no servidor
                {verificationStats.oldest && (
                  <> — a mais antiga de {formatDateTime(verificationStats.oldest)}</>
                )}
                . Com mais de 30 dias: <strong>{verificationStats.olderThan30}</strong>;
                com mais de 90: <strong>{verificationStats.olderThan90}</strong>.
              </p>
            )}
            <div className="facepg-purge">
              <label htmlFor="facepg-purge-days">Excluir verificações com mais de</label>
              <select
                id="facepg-purge-days"
                className="role-select"
                value={purgeDays}
                onChange={(e) => setPurgeDays(e.target.value)}
                disabled={purging}
              >
                <option value="365">1 ano</option>
                <option value="180">180 dias</option>
                <option value="90">90 dias</option>
                <option value="30">30 dias</option>
                <option value="7">7 dias</option>
                <option value="0">— excluir TODAS —</option>
              </select>
              <button
                className="btn-danger btn-sm"
                onClick={handlePurge}
                disabled={purging}
              >
                {purging ? 'Excluindo...' : 'Executar exclusão'}
              </button>
            </div>
          </div>
        </div>
        <div className="table-wrapper">
          <table className="modern-table">
            <thead>
              <tr>
                <th>Quando</th>
                <th>Pessoa</th>
                <th>Tipo</th>
                <th>Resultado</th>
                <th>Semelhança</th>
                <th>Fiscal</th>
                <th style={{ textAlign: 'right' }}>Evidência</th>
                <th style={{ textAlign: 'right' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {verifications.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center empty-state">
                    <strong>Nenhuma verificação registrada ainda</strong>
                    Cada conferência facial feita pelo fiscal aparece aqui.
                  </td>
                </tr>
              ) : (
                verificationsPagination.pageItems.map((v) => (
                  <tr key={v.id}>
                    <td className="col-time">{formatDateTime(v.createdAt)}</td>
                    <td className="cell-primary">{v.user?.name || v.userId}</td>
                    <td>{v.purpose === 'entry' ? 'Entrada' : 'Saída'}</td>
                    <td>
                      {v.success ? (
                        <span className="badge badge-success">Aprovada</span>
                      ) : (
                        <span className="badge badge-danger">Recusada</span>
                      )}
                    </td>
                    <td>
                      {Math.round(v.similarity * 100)}%{' '}
                      <small style={{ color: '#6b7280' }}>
                        (mín. {Math.round(v.threshold * 100)}%)
                      </small>
                    </td>
                    <td>{v.verifiedByName || '-'}</td>
                    <td style={{ textAlign: 'right' }}>
                      {v.photoPath ? (
                        <button className="btn-secondary btn-sm" onClick={() => handleShowEvidence(v)}>
                          Ver foto
                        </button>
                      ) : (
                        <small style={{ color: '#9ca3af' }}>-</small>
                      )}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <button
                        className="btn-danger btn-sm"
                        onClick={() => handleDeleteVerification(v)}
                        disabled={deletingId === v.id}
                        title="Excluir esta verificação e sua foto de evidência"
                      >
                        {deletingId === v.id ? 'Excluindo...' : 'Excluir'}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          id="face-verificacoes"
          page={verificationsPagination.page}
          totalPages={verificationsPagination.totalPages}
          total={verificationsPagination.total}
          from={verificationsPagination.from}
          to={verificationsPagination.to}
          pageSize={verificationsPagination.pageSize}
          pageSizeOptions={[25, 50, 100, 200]}
          onPageChange={verificationsPagination.setPage}
          onPageSizeChange={verificationsPagination.setPageSize}
          itemLabel="verificações"
        />
      </div>

      {managePerson && (
        <FacePhotosModal
          person={managePerson}
          canDelete
          onChanged={refreshStatus}
          onClose={() => setManagePerson(null)}
        />
      )}

      {evidenceUrl && (
        <div
          className="facepg-evidence-overlay"
          onClick={() => {
            URL.revokeObjectURL(evidenceUrl);
            setEvidenceUrl(null);
          }}
        >
          <img src={evidenceUrl} alt="Foto de evidência da verificação" />
        </div>
      )}
    </>
  );
};

export default FaceConfigPage;
